import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { productIdFormat } from "../ids";
import { MoneySchema, WeightSchema, WeightUnit } from "../outbound-labels/util";
import { RedoShopifyCountryCode } from "./fulfillment-country-code";
import { FulfillmentGroupData } from "./fulfillment-group";

export const FulfillmentOrderLineItemSchema = z.object({
  externalId: z.string(), // SHOULD BE GLOBALLY UNIQUE
  imageUrl: z.string().nullish(),
  customAttributes: z.array(z.object({ key: z.string(), value: z.string() })),
  // Redo business logic should only use quantity
  // unfulfilledQuantity and totalQuantity are provided by the marketplace and are used
  // in upsert logic to calculate the "quantity" of items when we split fulfillment orders across
  // multiple fulfillment groups (ex. partial fulfillments)
  totalQuantity: z.number(),
  unfulfilledQuantity: z.number(),
  quantity: z.number(),
  weight: z.object({
    unit: createEnum(WeightUnit, { enumName: "WeightUnit" }),
    value: z.number(),
  }),
  // These three IDs are nullish because if the product is deleted they all become null
  productId: z.string().nullish(),
  variantId: z.string().nullish(),
  inventoryItemId: z.string().nullish(),
  redoProductId: productIdFormat.schema.nullish(),
  inventoryItemWeight: WeightSchema.nullish(),
  lineItemId: z.string(),
  title: z.string(),
  productTitle: z.string(),
  variantTitle: z.string(),
  variantInventoryQuantity: z.number().nullish(),
  selectedOptions: z
    .array(z.object({ name: z.string(), value: z.string() }))
    .nullish(),
  requiresShipping: z.boolean().nullish(),
  sku: z.string().nullish(),
  unitPrice: MoneySchema,
  unitDiscountedPrice: MoneySchema,
  totalPrice: MoneySchema,
  totalDiscountedPrice: MoneySchema,
  totalTax: MoneySchema,
  hsCode: z.string().nullable(),
  countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }).nullish(),
  bin: z.string().nullish(),
  barcode: z.string().nullish(),
  productTags: z.array(z.string()).nullish(),
  vendor: z.string().nullish(),
  description: z.string().nullish(),
  notReceived: z.boolean().nullish(),
  productCustomDate1: z.string().nullish(),
  productCustomDate2: z.string().nullish(),
  productCustomDate3: z.string().nullish(),
  productCustomString1: z.string().nullish(),
  productCustomString2: z.string().nullish(),
  productCustomString3: z.string().nullish(),
  productCustomNumber1: z.string().nullish(),
  productCustomNumber2: z.string().nullish(),
  productCustomNumber3: z.string().nullish(),
  variantCustomDate1: z.string().nullish(),
  variantCustomDate2: z.string().nullish(),
  variantCustomDate3: z.string().nullish(),
  variantCustomString1: z.string().nullish(),
  variantCustomString2: z.string().nullish(),
  variantCustomString3: z.string().nullish(),
  variantCustomNumber1: z.string().nullish(),
  variantCustomNumber2: z.string().nullish(),
  variantCustomNumber3: z.string().nullish(),
  orderId: z.string().nullish(),
  lineItemNumber: z.string().nullish(),
  preSplitUnfulfilledQuantity: z.number().nullish(), // Used to ensure that we dont duplicate line items for split orders
  dimValue: z.number().nullish(),
  productType: z.string().nullish(),
});

export type FulfillmentOrderLineItem = z.infer<
  typeof FulfillmentOrderLineItemSchema
>;
export interface IFulfillmentOrderLineItem
  extends z.ZodType<FulfillmentOrderLineItem> {}

export type FulfillmentOrderLineItemWithOrderIds = FulfillmentOrderLineItem & {
  orderNames: string[];
};

export function getLineItemGroupingKey(
  item: FulfillmentOrderLineItem,
  config?: { usePrice?: boolean; degroupByCustomAttributes?: boolean },
): string {
  if (!item.variantId || !item.unitDiscountedPrice.amount) {
    return item.externalId;
  }
  const customAttributesKey = [...item.customAttributes]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((attr) => `${attr.key}:${attr.value}`)
    .join("|");

  let key = `${item.variantId}`;
  if (config?.usePrice) {
    key += `-${item.unitDiscountedPrice.amount}`;
  }
  if (config?.degroupByCustomAttributes) {
    key += `-${customAttributesKey}`;
  }
  return key;
}

// We could have the same item purchased on multiple orders that get merged together - this should reduce those line items to a single line item
export function groupFulfillmentOrderLineItems(
  items: readonly FulfillmentOrderLineItem[],
  config?: { usePrice?: boolean; degroupByCustomAttributes?: boolean },
): FulfillmentOrderLineItem[] {
  const reducedLineItems: FulfillmentOrderLineItem[] = [];
  const reducedLineItemsRec: Record<string, FulfillmentOrderLineItem> = {};

  for (const item of items) {
    const key = getLineItemGroupingKey(item, config);

    if (!reducedLineItemsRec[key]) {
      reducedLineItemsRec[key] = { ...item };
    } else {
      reducedLineItemsRec[key].totalQuantity += item.totalQuantity;
      reducedLineItemsRec[key].unfulfilledQuantity += item.unfulfilledQuantity;
      reducedLineItemsRec[key].quantity += item.quantity;
    }
  }

  return [...reducedLineItems, ...Object.values(reducedLineItemsRec)];
}

// Given a list of line items grouped together but that may not be grouped by fulfillment order, return a list of line items ungrouped. The list will only have one entry for each fulfillment order line item id
export function ungroupFulfillmentOrderLineItems(
  originalItems: FulfillmentOrderLineItem[],
  groupedItems: FulfillmentOrderLineItem[],
): FulfillmentOrderLineItem[] {
  const ungrouped: FulfillmentOrderLineItem[] = [];

  for (const originalItem of originalItems) {
    const matchingGroupedItem = groupedItems.find(
      (i) =>
        !!i.variantId &&
        i.variantId === originalItem.variantId &&
        i.unitDiscountedPrice.amount ===
          originalItem.unitDiscountedPrice.amount,
    );
    if (matchingGroupedItem && matchingGroupedItem.quantity > 0) {
      const quantityToTake = Math.min(
        originalItem.quantity,
        matchingGroupedItem.quantity,
      );

      // Remove the quantity from the grouped item and add that to the ungrouped item
      matchingGroupedItem.quantity -= quantityToTake;
      ungrouped.push({ ...originalItem, quantity: quantityToTake });
    }
  }

  for (const groupedItem of groupedItems) {
    if (groupedItem.quantity > 0) {
      ungrouped.push(groupedItem);
    }
  }

  return ungrouped;
}

export function getLineItemKey(item: FulfillmentOrderLineItem): string {
  return `${item.productId}${item.variantId ? `-${item.variantId}` : ""}`;
}

export function calculatePickedQuantitiesByGroupingKey(
  items: readonly FulfillmentOrderLineItem[],
  pickedQuantityByExternalId: Map<string, number>,
  config?: { usePrice?: boolean; degroupByCustomAttributes?: boolean },
): Map<string, number> {
  const pickedByGroupingKey = new Map<string, number>();

  for (const item of items) {
    const groupingKey = getLineItemGroupingKey(item, config);
    const pickedQty = pickedQuantityByExternalId.get(item.externalId) || 0;
    pickedByGroupingKey.set(
      groupingKey,
      (pickedByGroupingKey.get(groupingKey) || 0) + pickedQty,
    );
  }

  return pickedByGroupingKey;
}

export function getPickedMapByExternalId(
  fulfillmentGroup: FulfillmentGroupData,
): Map<string, number> {
  const pickSessionItemStatuses =
    fulfillmentGroup.pickSessionItemStatuses || [];
  const pickedMap = new Map<string, number>();

  for (const status of pickSessionItemStatuses) {
    pickedMap.set(
      status.fulfillmentOrderLineItemExternalId,
      status.quantityPicked,
    );
  }

  return pickedMap;
}

export function getMissingPickedItems(
  fulfillmentGroup: FulfillmentGroupData,
): FulfillmentOrderLineItem[] {
  const pickedByExternalId = getPickedMapByExternalId(fulfillmentGroup);

  const missingItems: FulfillmentOrderLineItem[] = [];
  for (const item of fulfillmentGroup.items) {
    // If item is not in pickSessionItemStatuses, treat as unpicked (quantity 0)
    const pickedQty = pickedByExternalId.get(item.externalId) || 0;

    if (pickedQty < item.quantity) {
      missingItems.push({ ...item, quantity: item.quantity - pickedQty });
    }
  }

  // Expand for consistency with item name column so that we show the total quantity of items in the column
  // We cannot use getExpandedItems function because it has a cache for the fulfillment group items and we need to display a subset of the items
  const expanded = missingItems.flatMap((item) =>
    Array.from({ length: item.quantity }, () => ({
      ...item,
      quantity: 1,
      totalQuantity: 1,
    })),
  );

  return expanded;
}
